import {
  createAsyncThunk,
  createSlice,
  SerializedError,
} from '@reduxjs/toolkit';
import {
  apiRequest,
  LOCAL_STORAGE_ACCESS_TOKEN_KEY,
  LOCAL_STORAGE_REFRESH_TOKEN_KEY,
} from '../helpers/api';

export const USERS_MAX_RESULTS = 10;

type SearchResults = {
  items: User[];
  totalItems: number;
};

export type UpdateUserDto = {
  disabled?: boolean;
};

export interface User {
  _id: string;
  microsoftUserId: string;
  firstName?: string;
  lastName?: string;
  profilePictureUrl?: string;
  email?: string;
  role?: string;
}

export interface Team {
  _id: string;
  teamId: string;
  groupId: string;
  enabledTeamsChannelsForBot: string[];
}

interface Tokens {
  accessToken: string;
  refreshToken?: string;
}

interface AuthState {
  user: User | null;
  users: { [userId: string]: User };
  members: User[] | null;
  team: Team | null;
  isLogged: boolean;
  loading: boolean;
  isInitialLoading: boolean;
  error: string | null;
  searchResult: {
    resultsUsers: User[];
    resultsCount: number;
  } | null;
  offset: number;
}

const initialState: AuthState = {
  isLogged: false,
  loading: false,
  isInitialLoading: true,
  user: null,
  users: {},
  members: null,
  team: null,
  error: null,
  searchResult: null,
  offset: 0,
};

const setAccessRefreshTokenAndGetUser = (
  accessToken: string,
  refreshToken?: string,
) => {
  localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY, refreshToken);
  }

  return apiRequest<User>('GET', '/auth/user');
};

/* Thunks */

export const updateUser = createAsyncThunk(
  'updateUser',
  async (
    payload: { updateUserDto: UpdateUserDto; id: string },
    { getState, dispatch },
  ) => {
    const { auth } = getState() as { auth: AuthState };
    const result = await apiRequest<User>(
      'PATCH',
      `/user/${payload.id}`,
      undefined,
      payload.updateUserDto,
    );
    if (payload.id === auth?.user?._id) {
      dispatch(fetchCurrentUser());
    }
    return result;
  },
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async () => {
    const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);

    if (!accessToken) {
      return null;
    }

    try {
      return await apiRequest<User>('GET', '/auth/user');
    } catch (error) {
      return null;
    }
  },
);

export const fetchCurrentTeamSettings = createAsyncThunk(
  'auth/fetchCurrentTeamSettings',
  async (teamId: string) => {
    return await apiRequest<Team>('GET', `/teams/microsoft/${teamId}`);
  },
);

export interface TeamDto {
  teamId: string;
  groupId: string;
  name?: string | null;
  enabledTeamsChannelsForBot: string[];
}

export const createNewTeam = createAsyncThunk(
  'auth/createNewTeam',
  async (createTeamDto: TeamDto) => {
    return await apiRequest<Team>('POST', '/teams', undefined, createTeamDto);
  },
);

export const updateTeamSettings = createAsyncThunk(
  'auth/updateTeamSettings',
  async (updateTeamDto: {
    teamId: string;
    enabledTeamsChannelsForBot: string[];
  }) => {
    return await apiRequest<Team>(
      'PATCH',
      `/teams/microsoft/${updateTeamDto.teamId}`,
      undefined,
      {
        enabledTeamsChannelsForBot: updateTeamDto.enabledTeamsChannelsForBot,
      },
    );
  },
);

export const findUserById = createAsyncThunk(
  'auth/findUserById',
  async (payload: { userId: string }, { dispatch, getState }) => {
    try {
      const result = await apiRequest<User>('GET', `/user/${payload.userId}`);
      const state: AuthState | null = getState() as AuthState;
      if (payload.userId === state?.user?._id) {
        dispatch(fetchCurrentUser());
      }
      return result;
    } catch (error) {
      return null;
    }
  },
);

export const getUsers = createAsyncThunk('auth/getUsers', async () => {
  try {
    return await apiRequest<User[]>('GET', `/user/`);
  } catch (error) {
    return null;
  }
});

export const findUsersByIds = createAsyncThunk(
  'auth/findUsersByIds',
  async (userIds: string[]) => {
    try {
      return await apiRequest<User[]>('POST', `/user/findUsers`, undefined, {
        userIds: userIds,
      });
    } catch (error) {
      return null;
    }
  },
);

export const getUsersMoreInformations = createAsyncThunk(
  'auth/getUsersMoreInformations',
  async (payload: { limit: number; offset: number }) => {
    try {
      return {
        ...(await apiRequest<SearchResults>(
          'GET',
          `/user/moreInformations`,
          payload,
        )),
        offset: payload.offset,
      };
    } catch (error) {
      return null;
    }
  },
);

export const signInUp = createAsyncThunk(
  'auth/signInUp',
  async ({
    microsoftAccessToken,
    groupId,
  }: {
    microsoftAccessToken: string;
    groupId: string;
  }) => {
    const { accessToken, refreshToken } = await apiRequest<Tokens>(
      'POST',
      '/auth/teams',
      undefined,
      { accessToken: microsoftAccessToken, groupId },
    );

    return await setAccessRefreshTokenAndGetUser(accessToken, refreshToken);
  },
);

/* Shared reducers */
const signInUpPendingReducer = (state: AuthState) => {
  state.isLogged = false;
  state.loading = true;
  state.error = null;
  state.user = null;
};

const signInUpFulfilledReducer = (
  state: AuthState,
  { payload }: { payload: User },
) => {
  state.isLogged = true;
  state.loading = false;
  state.user = payload;
};

const rejectedReducer = (
  state: AuthState,
  { error }: { error: SerializedError },
) => {
  state.loading = false;
  state.error = error.message || 'error';
};

/* Slice */

const receiveUsersReducer = (
  state: AuthState,
  { payload }: { payload: any },
) => {
  state.loading = false;
  if (payload) {
    state.offset = payload.offset;
    state.searchResult = {
      resultsCount: payload.totalItems,
      resultsUsers: payload.items,
    };
  }
};

const authSlice = createSlice({
  name: 'auth',
  reducers: {
    logout(state: AuthState) {
      state.user = null;
      state.isLogged = false;

      localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
      localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state: AuthState) => {
        state.isInitialLoading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state: AuthState, { payload }) => {
        state.isInitialLoading = false;
        state.user = payload;
        state.isLogged = !!payload;
      })
      .addCase(
        fetchCurrentTeamSettings.fulfilled,
        (state: AuthState, { payload }) => {
          state.team = payload;
        },
      )
      .addCase(createNewTeam.fulfilled, (state: AuthState, { payload }) => {
        state.team = payload;
      })
      .addCase(
        updateTeamSettings.fulfilled,
        (state: AuthState, { payload }) => {
          state.team = payload;
        },
      )
      .addCase(findUserById.pending, (state: AuthState) => {
        state.loading = true;
      })
      .addCase(findUserById.fulfilled, (state: AuthState, { payload }) => {
        if (payload) state.users = { ...state.users, [payload._id]: payload };
        state.loading = false;
      })
      .addCase(findUsersByIds.pending, (state: AuthState) => {
        state.loading = true;
      })
      .addCase(findUsersByIds.fulfilled, (state: AuthState, { payload }) => {
        if (!payload) return;
        payload.forEach((user) => {
          if (state.users) state.users = { ...state.users, [user._id]: user };
        });
        state.loading = false;
      })
      .addCase(findUsersByIds.rejected, rejectedReducer)
      .addCase(getUsers.pending, (state: AuthState) => {
        state.loading = true;
      })
      .addCase(getUsers.fulfilled, (state: AuthState, { payload }) => {
        if (!payload) return;
        payload.forEach((user) => {
          if (state.users) state.users[user._id] = user;
        });
      })
      .addCase(getUsers.rejected, rejectedReducer)
      .addCase(updateUser.pending, (state: AuthState) => {
        state.loading = true;
      })
      .addCase(updateUser.fulfilled, (state: AuthState, { payload }) => {
        if (!payload) return;
        state.users[payload._id] = payload;
      })
      .addCase(updateUser.rejected, rejectedReducer)
      .addCase(getUsersMoreInformations.pending, (state: AuthState) => {
        state.loading = true;
      })
      .addCase(getUsersMoreInformations.fulfilled, receiveUsersReducer)
      .addCase(getUsersMoreInformations.rejected, rejectedReducer)
      .addCase(signInUp.pending, signInUpPendingReducer)
      .addCase(signInUp.fulfilled, signInUpFulfilledReducer)
      .addCase(signInUp.rejected, rejectedReducer);
  },
  initialState,
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
